import React, { useState, useEffect } from "react";
import axios from "axios";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Editor } from "@tinymce/tinymce-react";
import { SingleImageDropzone } from "../DropzoneComponents";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

/* -------- TREE BUILD -------- */
const buildTree = (items, parent = null) =>
  items
    .filter((i) => i.parent_id === parent)
    .map((i) => ({ ...i, children: buildTree(items, i.id) }));

/* -------- PARENT CATEGORY DROPDOWN -------- */
const TreeDropdown = ({ items, selected, onSelect }) => {
  const [open, setOpen] = useState(false);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const renderTree = (nodes, level = 0) =>
    nodes.map((node) => (
      <div key={node.id}>
        <div
          onClick={() => {
            onSelect(node.id);
            setOpen(false);
          }}
          style={{
            padding: "6px 10px",
            paddingLeft: `${level * 22 + 10}px`,
            cursor: "pointer",
            background: node.id === selected ? "#dceeff" : "#fff",
            fontWeight: node.id === selected ? 600 : 400,
          }}
        >
          {level > 0 && <span style={{ marginRight: 6 }}>↳</span>}
          {node.title}
        </div>

        {node.children?.length > 0 && renderTree(node.children, level + 1)}
      </div>
    ));

  return (
    <div className="w-100 position-relative">
      <button
        className="btn btn-outline-secondary w-100 text-start"
        onClick={() => setOpen(!open)}
        type="button"
      >
        {selected
          ? getText("SELECTED_LABEL") +
            " " +
            items.find((i) => i.id === selected)?.title
          : getText("SELECT_PARENT_PLACEHOLDER")}
      </button>

      {open && (
        <div
          className="border rounded bg-white w-100 shadow-sm mt-1"
          style={{
            maxHeight: 300,
            overflowY: "auto",
            position: "absolute",
            zIndex: 999,
          }}
        >
          {renderTree(buildTree(items))}
        </div>
      )}
    </div>
  );
};

const StockCategoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const [parents, setParents] = useState([]);
  const [form, setForm] = useState({
    title: "",
    parent_id: null,
    brand_option: "hayir",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    status: "aktif",
    delete_image: false,
    is_main_category: "",
  });

  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  //  Child ID'leri bul — kendini ve çocuklarını parent seçiminden çıkaracağız
  const getAllChildIds = (items, parentId) => {
    let ids = [];
    const findChildren = (pid) => {
      const childs = items.filter((i) => i.parent_id === pid);
      childs.forEach((c) => {
        ids.push(c.id);
        findChildren(c.id);
      });
    };
    findChildren(parentId);
    return ids;
  };

  /*  DATA FETCH */
  useEffect(() => {
    const load = async () => {
      try {
        const catRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/${id}`,
        );
        const listRes = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/list-simple`,
        );

        const d = catRes.data;
        const all = listRes.data;

        const childIds = getAllChildIds(all, d.id);
        const filteredParents = all.filter(
          (c) => c.id !== d.id && !childIds.includes(c.id),
        );

        setParents(filteredParents);

        setForm({
          title: d.title,
          parent_id: d.parent_id,
          is_main_category: d.parent_id ? "no" : "yes",
          brand_option: d.brand_option,
          seo_title: d.seo_title,
          seo_description: d.seo_description,
          seo_keywords: d.seo_keywords,
          status: d.status,
          delete_image: false,
        });

        setContent(d.content || "");
        setPreviewImage(
          d.image ? `${process.env.REACT_APP_API_BASE_URL}${d.image}` : null,
        );
      } catch {
        toast.error(getText("CATEGORY_LIST_FETCH_ERROR"));
      }
    };

    load();
  }, [id]);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSettings(res.data))
      .catch(() => toast.error(getText("SETTINGS_FETCH_ERROR")));
  }, []);

  /*  SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const data = new FormData();
    Object.entries(form).forEach(([key, value]) => {
      if (key === "delete_image") return; // bunu sonra ekleyeceğiz
      data.append(key, value);
    });

    data.append("delete_image", form.delete_image ? "true" : "false");

    data.append("content", content);
    if (file) data.append("image", file);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/${id}`,
        data,
      );
      toast.success(getText("CATEGORY_CREATED_SUCCESS"));
      navigate("/categories/table");
    } catch {
      toast.error(getText("CATEGORY_CREATE_FAILED"));
    }
  };

  return (
    <div className="container mt-4">
      <form className="card p-4" onSubmit={handleSubmit}>

        {/* Title */}
        <label className="form-label fw-bold">{getText("CATEGORY_NAME_LABEL")}</label>
        <input
          className="form-control mb-3"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
        />
        <label className="form-label fw-bold"> {getText("CATEGORY_POSITION_LABEL")}</label>
        <select
          className="form-select mb-3"
          value={form.is_main_category}
          onChange={(e) =>
            setForm({
              ...form,
              is_main_category: e.target.value,
              parent_id: e.target.value === "yes" ? null : form.parent_id,
            })
          }
        >
          <option value="yes">{getText("MAIN_CATEGORY_OPTION")}</option>
          <option value="no">{getText("SUB_CATEGORY_OPTION")}</option>
        </select>

        {form.is_main_category === "no" && (
          <>
            <label className="form-label fw-bold"> {getText("SELECT_PARENT_CATEGORY")}</label>
            <TreeDropdown
              items={parents}
              selected={form.parent_id}
              onSelect={(id) => setForm({ ...form, parent_id: id })}
            />
          </>
        )}

        {/* Resim */}
        <label className="form-label fw-bold mb-3">
            {getText("CATEGORY_IMAGE_LABEL")}  ({settings?.category_image_width}x
          {settings?.category_image_height})
        </label>

        {settings ? (
          <SingleImageDropzone
            fieldName="image"
            fieldValue={file || previewImage}
            setFieldValues={(fn) => {
              // Dropzone component bize ya file return ediyor ya null
              const val =
                typeof fn === "function" ? fn({ image: file }).image : fn.image;
              if (val === null) {
                setPreviewImage(null);
                setForm((p) => ({ ...p, delete_image: true }));
              } else {
                setPreviewImage(null); // eski resmi sıfırla
                setFile(val);
                setForm((p) => ({ ...p, delete_image: false }));
              }
            }}
            required={false}
            maxWidth={Number(settings.category_image_width)}
            maxHeight={Number(settings.category_image_height)}
          />
        ) : (
          <div>{getText("SETTINGS_LOADING")}</div>
        )}

        <label className="form-label mt-3">{getText("CONTENT_LABEL")}</label>

        {/* Editor */}
        <Editor
          tinymceScriptSrc="https://cdnjs.cloudflare.com/ajax/libs/tinymce/7.6.1/tinymce.min.js"
          value={content}
          onEditorChange={setContent}
          init={{
            promotion: false,
            height: 400,
            plugins: [
              "advlist",
              "autolink",
              "link",
              "image",
              "lists",
              "charmap",
              "preview",
              "anchor",
              "pagebreak",
              "searchreplace",
              "wordcount",
              "visualblocks",
              "code",
              "fullscreen",
              "insertdatetime",
              "media",
              "table",
              "emoticons",
              "help",
            ],
            toolbar:
              "undo redo | styles | bold italic | alignleft aligncenter alignright alignjustify | " +
              "bullist numlist outdent indent | link image | " +
              "print preview media fullscreen code | forecolor backcolor emoticons | help",
            content_style:
              "body { font-family:Helvetica,Arial,sans-serif; font-size:16px }",
            image_advtab: true,
            automatic_uploads: false,
            images_upload_url: `${process.env.REACT_APP_API_BASE_URL}/api/module-data/upload`,
            images_upload_handler: null,
          }}
        />

        {/* SEO */}
        <label className="form-label fw-bold mt-3">{getText("SEO_TITLE_LABEL")}</label>
        <input
          className="form-control"
          value={form.seo_title}
          onChange={(e) => setForm({ ...form, seo_title: e.target.value })}
        />

        <label className="form-label fw-bold mt-2">{getText("SEO_DESCRIPTION_LABEL")}</label>
        <textarea
          className="form-control"
          rows="2"
          value={form.seo_description}
          onChange={(e) =>
            setForm({ ...form, seo_description: e.target.value })
          }
        />

        <label className="form-label fw-bold mt-2">{getText("SEO_KEYWORDS_LABEL")}</label>
        <input
          className="form-control"
          value={form.seo_keywords}
          onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
        />
        {/* Marka */}
        <label className="form-label fw-bold mt-3">{getText("BRAND_SELECTION_LABEL")}</label>
        <select
          className="form-select mb-3"
          value={form.brand_option}
          onChange={(e) => setForm({ ...form, brand_option: e.target.value })}
        >
          <option value="hayir">{getText("NO_LABEL")}</option>
          <option value="evet">{getText("YES_LABEL")}</option>
        </select>
        {/* Status */}
        <label className="form-label fw-bold mt-3">{getText("STATUS_LABEL")}</label>
        <select
          className="form-select"
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
        >
          <option value="aktif">{getText("STATUS_ACTIVE")}</option>
          <option value="pasif"> {getText("STATUS_PASSIVE")}</option>
        </select>

        <div className="d-flex justify-content-center">
          <button className="btn btn-primary"> {getText("SAVE")}</button>
        </div>
      </form>
    </div>
  );
};

export default StockCategoryEdit;
